import React, { useState } from "react";

/**
 * ShowcaseLogo — Landing Page Polish 4 (final correction pass).
 *
 * Deterministic, LOCAL-only company logo tiles for the seven
 * showcase tickers (PYPL, MSFT, NEE, JPM, JNJ, CAT, AAPL). No
 * network request. No lazy resolution.
 *
 * Two-layer strategy so we never render a text monogram as the
 * expected state:
 *
 *   1. Primary: an <img> pointing at a bundled asset served from
 *      `/showcase-logos/{ticker}.svg`. These files live under
 *      /app/frontend/public/showcase-logos/. If a licensed asset
 *      file is present it is used immediately (loading="eager",
 *      fetchpriority="high", decoding="sync").
 *
 *   2. Fallback: a stylized brand-color tile with a discreet
 *      ticker glyph. Rendered only if the primary <img> fires
 *      onError (i.e. the licensed asset file has not yet been
 *      dropped in).
 *
 * The container reserves the same fixed size, radius, border and
 * background for both layers so there is zero layout shift when
 * the primary asset resolves.
 */

// Brand palette — public brand colors used only for the fallback tile.
const PALETTE = {
  PYPL: { bg: "#003087", fg: "#009CDE" },
  MSFT: { bg: "#242424", fg: "#F25022" },
  NEE:  { bg: "#003462", fg: "#66BE31" },
  JPM:  { bg: "#0F1E3D", fg: "#B49658" },
  JNJ:  { bg: "#CB0032", fg: "#FFFFFF" },
  CAT:  { bg: "#FFC91E", fg: "#111111" },
  AAPL: { bg: "#111111", fg: "#DADADA" },
};

const SIZES = {
  xs: { outer: "h-6 w-6 rounded-md" },
  sm: { outer: "h-8 w-8 rounded-lg" },
  md: { outer: "h-10 w-10 rounded-xl" },
};

// Public path of the bundled asset directory. Files are looked up by
// lowercase ticker slug so an owner-supplied file dropped in as
// `pypl.svg` is used automatically without a code change.
function assetSrc(ticker) {
  if (!ticker) return null;
  return `/showcase-logos/${String(ticker).toLowerCase()}.svg`;
}

function FallbackTile({ ticker }) {
  const p = PALETTE[ticker];
  if (!p) {
    return (
      <span aria-hidden className="text-[10px] font-semibold text-foreground/80 select-none">
        {ticker?.slice(0, 2) || "?"}
      </span>
    );
  }
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%" role="img" aria-hidden="true">
      <rect x="0" y="0" width="40" height="40" rx="8" fill={p.bg} />
      <text
        x="20"
        y="25"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif"
        fontWeight="700"
        fontSize="13"
        fill={p.fg}
        letterSpacing="0.5"
      >
        {ticker}
      </text>
    </svg>
  );
}

export function ShowcaseLogo({ ticker, name, size = "xs", testId }) {
  const preset = SIZES[size] || SIZES.xs;
  const src = assetSrc(ticker);
  const alt = (name && `${name} logo`) || (ticker && `${ticker} logo`) || "Company logo";
  const [broken, setBroken] = useState(false);

  const showImage = !!src && !broken;

  return (
    <div
      className={`relative shrink-0 overflow-hidden flex items-center justify-center border border-border/70 bg-muted/40 ${preset.outer}`}
      data-testid={testId || (ticker ? `showcase-logo-${String(ticker).toUpperCase()}` : "showcase-logo")}
      data-logo-state={showImage ? "image" : "fallback"}
      data-eager="true"
      aria-label={alt}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt}
          loading="eager"
          decoding="sync"
          // Non-standard hint on some browsers — kept as string for React 17+
          fetchpriority="high"
          className="h-full w-full object-contain p-0.5"
          onError={() => setBroken(true)}
          data-testid="showcase-logo-img"
        />
      ) : (
        <FallbackTile ticker={ticker} />
      )}
    </div>
  );
}

export const SHOWCASE_LOGO_TICKERS = Object.keys(PALETTE);

export default ShowcaseLogo;
